import { useState, useEffect } from 'react';
import { useAuthState } from './useAuthState';
import { messageService } from '@/services/messageService';

interface MessageNotification {
  unreadCount: number;
  lastConversationId: number | null;
  hasUnreadMessages: boolean;
}

export const useMessageNotifications = () => {
  const [notification, setNotification] = useState<MessageNotification>({
    unreadCount: 0,
    lastConversationId: null,
    hasUnreadMessages: false,
  });
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, userId } = useAuthState();

  const checkUnreadMessages = async () => {
    if (!isAuthenticated || !userId) {
      setNotification({
        unreadCount: 0,
        lastConversationId: null,
        hasUnreadMessages: false,
      });
      setLoading(false);
      return;
    }

    try {
      console.log('🔔 Verificando mensagens não lidas para usuário:', userId);
      
      // Buscar conversas com mensagens não lidas
      const conversations = await messageService.getConversations(userId);
      const list = Array.isArray(conversations) ? conversations : [];
      
      let totalUnread = 0;
      let lastConversationWithUnread: number | null = null;
      
      list.forEach((conversation) => {
        if (conversation.unread_count > 0) {
          totalUnread += conversation.unread_count;
          if (!lastConversationWithUnread) {
            lastConversationWithUnread = conversation.id;
          }
        }
      });

      const hasUnread = totalUnread > 0;
      
      console.log('📊 Resultado da verificação:', {
        totalUnread,
        lastConversationId: lastConversationWithUnread,
        hasUnread
      });

      setNotification({
        unreadCount: totalUnread,
        lastConversationId: lastConversationWithUnread,
        hasUnreadMessages: hasUnread,
      });
    } catch (error) {
      console.error('❌ Erro ao verificar mensagens não lidas:', error);
      setNotification({
        unreadCount: 0,
        lastConversationId: null,
        hasUnreadMessages: false,
      });
    } finally {
      setLoading(false);
    }
  };

  // Verificar mensagens não lidas quando o usuário estiver autenticado
  useEffect(() => {
    if (isAuthenticated && userId) {
      checkUnreadMessages();
      
      // Verificar a cada 30 segundos
      const interval = setInterval(checkUnreadMessages, 30000);
      
      return () => clearInterval(interval);
    } else {
      setNotification({
        unreadCount: 0,
        lastConversationId: null,
        hasUnreadMessages: false,
      });
      setLoading(false);
    }
  }, [isAuthenticated, userId]);

  // Listener para eventos de atualização de mensagens
  useEffect(() => {
    const handleMessageUpdate = () => {
      console.log('📨 Evento de atualização de mensagem recebido');
      checkUnreadMessages();
    };

    window.addEventListener('messageUpdated', handleMessageUpdate);
    
    return () => {
      window.removeEventListener('messageUpdated', handleMessageUpdate);
    };
  }, []);

  return {
    ...notification,
    loading,
    refreshNotifications: checkUnreadMessages,
  };
};
